import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { mainCta } from '@/mocks/cta';
import { categories } from '@/mocks/home';

interface FooterProps {
  currentLang: string;
}

export default function Footer({ currentLang }: FooterProps) {
  const { t } = useTranslation('home');

  // Internal links point only to real existing pages
  const footerColumns = [
    {
      title: t('footer_spreadsheet'),
      links: [
        { label: t('nav_spreadsheet'), to: `/${currentLang}/allchinabuy-spreadsheet/` },
        { label: t('nav_shopping_guide'), to: `/${currentLang}/guides/` },
        { label: t('nav_blog'), to: `/${currentLang}/blog/` },
      ],
    },
    {
      title: t('footer_guides'),
      links: [
        { label: t('guides_qc'), to: `/${currentLang}/blog/allchinabuy-qc-guide/` },
        { label: t('guides_shipping'), to: `/${currentLang}/shipping-cost/` },
        { label: t('guides_seller'), to: `/${currentLang}/blog/finding-trusted-sellers-allchinabuy/` },
        { label: t('guides_safety'), to: `/${currentLang}/safety/` },
      ],
    },
    {
      title: t('footer_reviews'),
      links: [
        { label: t('nav_review'), to: `/${currentLang}/review/` },
        { label: t('nav_shipping'), to: `/${currentLang}/shipping-cost/` },
        { label: t('nav_safety'), to: `/${currentLang}/safety/` },
      ],
    },
    {
      title: t('footer_resources'),
      links: [
        { label: t('nav_blog'), to: `/${currentLang}/blog/` },
        { label: t('nav_spreadsheet'), to: `/${currentLang}/allchinabuy-spreadsheet/` },
        { label: t('nav_home'), to: `/${currentLang}/` },
      ],
    },
    {
      title: t('footer_shipping'),
      links: categories.slice(0, 5).map((cat) => ({ label: t(cat.name), href: cat.url })),
    },
  ];

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'da', label: 'Dansk' },
  ];

  return (
    <footer className="bg-foreground-950 pt-16 md:pt-20 pb-8">
      <div className="w-full px-4 sm:px-6 lg:px-10 xl:px-16 max-w-7xl mx-auto">
        {/* Main Site CTA */}
        <div className="bg-foreground-900/50 rounded-xl p-6 md:p-8 mb-12 md:mb-16 border border-foreground-800/50">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-lg md:text-xl font-semibold text-background-50 mb-1">
                AllChinaBuy Spreadsheet
              </h3>
              <p className="text-foreground-400 text-sm">
                Visit our main website for full product catalog and shopping
              </p>
            </div>
            <a
              href={mainCta.mainSite}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-label font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('btn_main_site')}
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>

        {/* Footer Columns */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-10 mb-12 md:mb-16">
          {footerColumns.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading text-sm font-semibold text-background-50 mb-4 uppercase tracking-wider">
                {col.title}
              </h4>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {'to' in link ? (
                      <Link
                        to={link.to}
                        className="text-foreground-400 text-sm hover:text-background-50 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-foreground-400 text-sm hover:text-background-50 transition-colors"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Language Selector */}
        <div className="border-t border-foreground-800/50 pt-8 mb-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-foreground-400 text-xs font-semibold uppercase tracking-wider">Languages:</span>
            {languages.map((lang) => (
              <Link
                key={lang.code}
                to={`/${lang.code}/`}
                className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                  currentLang === lang.code
                    ? 'bg-primary-500/20 text-primary-400 font-medium'
                    : 'text-foreground-400 hover:text-background-50 hover:bg-foreground-800/50'
                }`}
              >
                {lang.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-foreground-800/50 pt-6">
          <p className="text-foreground-500 text-xs leading-relaxed mb-4">
            {t('footer_disclaimer_text')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-foreground-600 text-sm font-medium">
              {t('footer_copyright')}
            </p>
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/safety/`} className="text-foreground-500 text-xs hover:text-background-50 transition-colors">{t('footer_safety')}</Link>
              <Link to={`/${currentLang}/review/`} className="text-foreground-500 text-xs hover:text-background-50 transition-colors">{t('footer_reviews')}</Link>
              <Link to={`/${currentLang}/blog/`} className="text-foreground-500 text-xs hover:text-background-50 transition-colors">{t('nav_blog')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}